import { useState } from "react";
import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { toggleSx } from "../BaseMapToggle";
import { useTranslation } from "react-i18next";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";

// The name with the full ID.
export function NameWithId({ name, id }) {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();
  const copy = (event) => {
    event.stopPropagation(); // the row opens the details on click
    navigator.clipboard?.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
        {name || "—"}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography
          sx={{ fontSize: 11, color: "text.disabled", fontFamily: "monospace" }}
        >
          {id}
        </Typography>
        <Tooltip title={copied ? t("terms.copied") : t("terms.copyId")}>
          <IconButton
            size="small"
            aria-label={t("terms.copyId")}
            onClick={copy}
            sx={{ p: 0.25 }}
          >
            {copied ? (
              <CheckOutlinedIcon sx={{ fontSize: 13, color: "success.main" }} />
            ) : (
              <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export function AlertsBadge({ hasAlerts }) {
  const { t } = useTranslation();
  const Icon = hasAlerts
    ? NotificationsActiveOutlinedIcon
    : NotificationsNoneOutlinedIcon;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: "999px",
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
        color: hasAlerts ? "#b26a00" : "text.secondary",
        bgcolor: hasAlerts ? "rgba(255, 152, 0, 0.14)" : "#f3f4f6",
      }}
    >
      <Icon sx={{ fontSize: 14 }} />
      {hasAlerts
        ? t("dashboard.alertsBadge.open")
        : t("dashboard.alertsBadge.none")}
    </Box>
  );
}

// `options`: [{ key }], with "alerts" / "compliant" as keys.
export function TableFilters({ options, selected, onChange }) {
  const { t } = useTranslation();
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={selected[0] ?? "all"}
      onChange={(_, value) => value && onChange(value === "all" ? [] : [value])}
      aria-label={t("dashboard.filters.label")}
      sx={[
        toggleSx,
        {
          bgcolor: "white",
          p: 0.5,
          borderRadius: "10px",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
          "& .MuiToggleButton-root": { px: 1.5, py: 0.5, fontSize: 13 },
        },
      ]}
    >
      <ToggleButton value="all">{t("dashboard.filters.all")}</ToggleButton>
      {options.map(({ key }) => (
        <ToggleButton key={key} value={key}>
          {t(`dashboard.filters.options.${key}`)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
